import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './Layout.css';
import '../pages/Landing.css';

/**
 * Shared header for public pages (Blog, HowItWorks, etc.).
 * When the user is authenticated it renders the same green header with
 * hamburger menu as Layout.tsx so the experience is consistent across
 * all pages.  When logged out it renders the standard landing nav.
 */
function PublicHeader() {
  const navigate = useNavigate();
  const { logout, user, isAuthenticated } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const handleLogout = () => {
    setMenuOpen(false);
    logout();
    navigate('/');
  };

  // Close dropdown on outside click (authenticated state)
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  if (isAuthenticated) {
    return (
      <header className="header">
        <div className="container flex-between">
          <Link to="/settings" className="logo">
            <img src="/mosaic-icon.png" alt="mosaime" className="logo-icon-img" />
            <span className="logo-text">mosaime</span>
          </Link>

          <div className="header-menu" ref={menuRef}>
            <button
              className="header-hamburger"
              aria-label="Open menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((s) => !s)}
            >
              <span /><span /><span />
            </button>

            {menuOpen && (
              <div className="header-dropdown">
                {user?.name && <div className="dropdown-name">{user.name}</div>}
                <Link to="/settings" className="dropdown-link" onClick={() => setMenuOpen(false)}>Settings</Link>
                <Link to="/feedback" className="dropdown-link" onClick={() => setMenuOpen(false)}>Feedback</Link>
                <Link to="/blog" className="dropdown-link" onClick={() => setMenuOpen(false)}>Blog</Link>
                <button className="dropdown-link dropdown-logout" onClick={handleLogout}>Logout</button>
              </div>
            )}
          </div>
        </div>
      </header>
    );
  }

  // Unauthenticated: standard landing nav
  return (
    <nav className="landing-nav">
      <div className="landing-container nav-inner">
        <Link to="/" className="logo">
          <img src="/mosaic-icon.png" alt="mosaime" className="logo-icon-img" />
          <span className="logo-text">mosaime</span>
        </Link>

        <div className="nav-links-desktop">
          <Link to="/features" className="nav-link">Features</Link>
          <Link to="/how-it-works" className="nav-link">How It Works</Link>
          <Link to="/philosophy" className="nav-link">Philosophy</Link>
          <Link to="/faq" className="nav-link">FAQ</Link>
          <Link to="/blog" className="nav-link">Blog</Link>
          <Link to="/login" className="nav-link">Log in</Link>
          <Link to="/signup" className="nav-link nav-cta">Get Started</Link>
        </div>

        <button
          className="hamburger"
          aria-label="Open menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((s) => !s)}
        >
          <span className="hamburger-box">
            <span className="hamburger-inner" />
          </span>
        </button>

        {menuOpen && <div className="menu-backdrop" onClick={() => setMenuOpen(false)} />}
        <div className={`mobile-menu ${menuOpen ? 'open' : ''}`} role="menu">
          <button className="close-btn" onClick={() => setMenuOpen(false)} aria-label="Close menu">×</button>
          <Link to="/features" className="menu-link" onClick={() => setMenuOpen(false)}>Features</Link>
          <Link to="/how-it-works" className="menu-link" onClick={() => setMenuOpen(false)}>How It Works</Link>
          <Link to="/philosophy" className="menu-link" onClick={() => setMenuOpen(false)}>Philosophy</Link>
          <Link to="/faq" className="menu-link" onClick={() => setMenuOpen(false)}>FAQ</Link>
          <Link to="/login" className="menu-link" onClick={() => setMenuOpen(false)}>Log in</Link>
          <Link to="/signup" className="menu-link" onClick={() => setMenuOpen(false)}>Sign up</Link>
          <Link to="/blog" className="menu-link" onClick={() => setMenuOpen(false)}>Blog</Link>
        </div>
      </div>
    </nav>
  );
}

export default PublicHeader;
